/**
 * Stage 3 of the hybrid track-changes engine — the "Two-Pass Hybrid" char explosion.
 *
 * Stage 2 produces whole-word ops. Where a `delete` is immediately followed by an `insert` (a word
 * replacement), a character-level redline can read better than striking a whole word and inserting
 * the whole new one — but ONLY when the new word genuinely **extends** the old one, so the old word
 * is fully kept and merely grown: `agree` → `disagree` reads well as *insert* `dis` + *keep* `agree`;
 * `nation` → `national` as *keep* `nation` + *insert* `al`.
 *
 * Rule (deliberately conservative): explode iff the new (trimmed) word is **longer** than the old
 * one AND the old word is a **prefix or suffix** of it. That is exactly "extend an existing word".
 * Everything else — a word getting shorter (`forth` → `for`), an internal typo (`recieve` →
 * `receive`), or two different words — stays a clean whole-word delete+insert.
 *
 * Why not explode when a word gets shorter or changes internally: that reuses characters from a word
 * that is really being deleted (e.g. keeping `for` out of a struck `forth`), which reads as
 * "un-deleting" a fragment — non-human-readable. Requiring the old word to be fully preserved as a
 * prefix/suffix guarantees we never partially strike it.
 *
 * Surrounding whitespace is preserved exactly and reconciled separately, so the whole-array
 * reconstruction invariants survive explosion: `keep`+`delete` still rebuilds the baseline,
 * `keep`+`insert` still rebuilds the target.
 *
 * Pure function, no Word dependency, unit-tested.
 */

import { diffChars } from 'diff';
import { Op } from './types';
import { computePunctuationAffixes } from '../../word/utils/punctuationAffix';

/**
 * Walk the op array; explode each adjacent `delete`→`insert` pair.
 *
 * Two strategies, in order:
 *  1. **Punctuation-only affix extraction.** If the pair shares a char-level prefix and/or suffix
 *     such that the DELETE remainder contains no letter/digit character, keep those affixes as
 *     `keep` and emit a minimal delete+insert for the middle. This turns e.g.
 *       delete `המלווה".` + insert `המלווה" [ראה סעיף 4.1 …]`
 *     into `keep 'המלווה"'` + `delete '.'` + `insert ' [ראה סעיף 4.1 …]'`, so the shared word is
 *     not needlessly struck and re-inserted just because a period differs. The "no letter/digit in
 *     the delete remainder" gate preserves the hard invariant that we NEVER partially strike a real
 *     word (guarding the `forth`→`for` / `recieve`→`receive` cases).
 *  2. **Word extension.** Otherwise, fall back to the classic explode-when-extending rule below.
 */
export function explodePairs(ops: Op[]): Op[] {
  const out: Op[] = [];
  let i = 0;
  while (i < ops.length) {
    const cur = ops[i];
    const next = ops[i + 1];
    if (cur.kind === 'delete' && next && next.kind === 'insert') {
      const affixed = extractPunctuationAffixes(cur.value, next.value);
      if (affixed) {
        out.push(...affixed);
      } else {
        const exploded = maybeExplode(cur.value, next.value);
        if (exploded) out.push(...exploded);
        else out.push(cur, next);
      }
      i += 2;
    } else {
      out.push(cur);
      i += 1;
    }
  }
  return coalesce(out);
}

/**
 * Extract the longest common char-level prefix and suffix of a delete/insert pair as `keep` ops
 * via the shared `computePunctuationAffixes` helper (see that module for the safety rationale).
 * Returns `null` when nothing can be extracted safely — the caller then falls back to the classic
 * "extend an existing word" explode rule below.
 */
function extractPunctuationAffixes(deleteVal: string, insertVal: string): Op[] | null {
  const affixes = computePunctuationAffixes(deleteVal, insertVal);
  if (!affixes) return null;
  const result: Op[] = [];
  if (affixes.prefix !== '') result.push({ kind: 'keep', value: affixes.prefix });
  if (affixes.deleteMiddle !== '') result.push({ kind: 'delete', value: affixes.deleteMiddle });
  if (affixes.insertMiddle !== '') result.push({ kind: 'insert', value: affixes.insertMiddle });
  if (affixes.suffix !== '') result.push({ kind: 'keep', value: affixes.suffix });
  return result;
}

/**
 * Decide and perform explosion for one delete/insert pair. Returns the char-level op sequence, or
 * `null` to leave the pair as a whole-word replacement.
 */
function maybeExplode(deleteValue: string, insertValue: string): Op[] | null {
  const dCore = deleteValue.trim();
  const iCore = insertValue.trim();
  // Whitespace-only side: nothing word-like to compare; leave as-is.
  if (dCore === '' || iCore === '') return null;

  // Explode ONLY when the new word extends the old one (old word fully kept as a prefix/suffix).
  // This never partially strikes the old word, so it can't "un-delete" a fragment of struck text.
  const isExtension = iCore.length > dCore.length && (iCore.startsWith(dCore) || iCore.endsWith(dCore));
  if (!isExtension) return null;

  const dLead = deleteValue.slice(0, deleteValue.length - deleteValue.trimStart().length);
  const dTrail = deleteValue.slice(deleteValue.trimEnd().length);
  const iLead = insertValue.slice(0, insertValue.length - insertValue.trimStart().length);
  const iTrail = insertValue.slice(insertValue.trimEnd().length);

  const result: Op[] = [];
  reconcileAffix(result, dLead, iLead);
  for (const change of diffChars(dCore, iCore)) {
    if (change.added) result.push({ kind: 'insert', value: change.value });
    else if (change.removed) result.push({ kind: 'delete', value: change.value });
    else result.push({ kind: 'keep', value: change.value });
  }
  reconcileAffix(result, dTrail, iTrail);
  return result;
}

/**
 * Emit ops for a leading/trailing whitespace affix that surrounds the exploded word: `keep` it when
 * both sides share it, otherwise `delete` the baseline's and `insert` the target's. Preserves both
 * reconstruction invariants for the affix.
 */
function reconcileAffix(out: Op[], baselineAffix: string, targetAffix: string): void {
  if (baselineAffix === targetAffix) {
    if (baselineAffix !== '') out.push({ kind: 'keep', value: baselineAffix });
    return;
  }
  if (baselineAffix !== '') out.push({ kind: 'delete', value: baselineAffix });
  if (targetAffix !== '') out.push({ kind: 'insert', value: targetAffix });
}

/** Merge adjacent same-kind ops into one, concatenating values. */
function coalesce(ops: Op[]): Op[] {
  const out: Op[] = [];
  for (const op of ops) {
    const prev = out[out.length - 1];
    if (prev && prev.kind === op.kind) prev.value += op.value;
    else out.push({ ...op });
  }
  return out;
}
